<template>
	<div
		v-if="entity"
		class="upgrade-box"
		:class="[
			`upgrade-box--${type} upgrade-box--${skin}`,
			{ 'upgrade-box--disabled': isDisabled },
		]"
		:data-event-type="eventType"
		@click="handleToast"
	>
		<div class="upgrade-box__image" :class="`icon-${iconName}-60`">
			<div v-if="notification" class="notification">
				{{ notification }}
			</div>
		</div>
		<div class="upgrade-box__content-wrapper">
			<div class="upgrade-box__content">
				<div class="content-container">
					<span class="upgrade-box__heading">{{ heading }}</span>
					<div
						v-if="type === TileUpgradeBoxTypes.LIST"
						class="upgrade-box__description upgrade-box__description-effect"
					>
						<span v-if="description">{{ tileDescription }}</span>
					</div>
					<div
						v-if="type === TileUpgradeBoxTypes.LIST"
						class="upgrade-box__description"
					>
						<TileAggregationDetails
							:entity="entity"
							:isUpgrade="true"
							:isPromotion="isPromotion"
						/>
						<div
							class="upgrade-box__limit"
							v-if="entity.buildingLimit"
						>
							<div>{{ `${translate("ui.limit")}:` }}</div>
							<div
								:class="{
									'full-limit':
										entity.sameBuildingCount +
											(buildingsStorageStore?.[
												entity.frameName
											] || 0) >=
										entity.buildingLimit,
								}"
							>
								{{
									`${entity.sameBuildingCount}/${entity.buildingLimit}`
								}}
							</div>
						</div>
					</div>
				</div>
				<div
					class="upgrade-box__required"
					v-if="
						entity &&
						!entity.isLevelRequirementSatisfied(
							entity,
							// true,
						) &&
						type === TileUpgradeBoxTypes.LIST
					"
				>
					{{
						translate("ui.available_from_level").replace(
							"{number}",
							requiredLevel.toString(),
						)
					}}
				</div>
				<div
					class="upgrade-box__required"
					v-else-if="
						fewShipyards && type === TileUpgradeBoxTypes.LIST
					"
				>
					{{ translate("ui.build_more_shipyards") }}
				</div>
				<div
					class="upgrade-box__required"
					v-else-if="
						hasEnoughtSettlementLevelForPremiumRoads &&
						type === TileUpgradeBoxTypes.LIST
					"
				>
					{{ roadLimitText }}
				</div>
				<div
					v-else-if="
						(price || relativeConsumption) &&
						type === TileUpgradeBoxTypes.LIST &&
						isContextAvailable
					"
					class="upgrade-box__price"
				>
					<span
						v-if="
							(price !== null &&
								!hidePrice &&
								toArray(price).length) ||
							relativeConsumption?.size
						"
						class="upgrade-box__price-label price-label"
					>
						{{ translate("ui.price") }}:
					</span>
					<template v-if="!hidePrice && price">
						<template
							v-for="(value, currency) in price"
							:key="currency"
						>
							<AppPrice
								v-if="value"
								:amount="value"
								:currency="currency"
								:iconSize="14"
								:fontSize="14"
							/>
						</template>
					</template>
					<template v-if="relativeConsumption">
						<AppPrice
							v-for="[currency, value] in relativeConsumption"
							:key="currency"
							:amount="value"
							:currency="resourceTypeToCurrencyType(currency)"
							:iconSize="14"
							:fontSize="14"
						/>
					</template>
				</div>
			</div>
		</div>
		<div
			class="event-building-label"
			v-if="
				skin === TileUpgradeBoxSkinTypes.BUILDING_EVENT &&
				context !== UIContexts.SEARCH &&
				type !== TileUpgradeBoxTypes.GRID
			"
		>
			<span>
				{{ translate("event.event_building") }}
			</span>
		</div>
		<ToastMessage
			width="auto"
			:translatedText="toastMessage"
			:showMessage="showToast && !!toastMessage"
		/>
		<AppNotification
			v-if="
				!tilerepoSpecialUnitsFrameNames.has(entity?.frameName) &&
				tilerepoFriendlySupplyUnitsFrameNames.has(entity?.frameName) &&
				supplyLimit <= 0
			"
			class="red-notification"
			value="!"
			:type="StatusTypes.WARNING"
		/>
		<div
			v-if="
				tilerepoFriendlyUnitsFrameNames.has(entity?.frameName) &&
				!isSupplySatisfied &&
				type === TileUpgradeBoxTypes.LIST &&
				!isLevelUp &&
				context !== UIContexts.UNLOCK_HISTORY
			"
			class="no-supply"
			:class="entity?.frameName"
		>
			{{ translate("ui.insufficient_supply_limit") }}
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import TileAggregationDetails from "@/components/TileAggregationDetails.vue";
import ToastMessage from "@/components/ToastMessage.vue";
import {
	FrameName,
	tilerepoBigFishingBoatsFrameNames,
	tilerepoBlastFurnacesFrameNames,
	tilerepoFishingBoatsFrameNames,
	tilerepoFishTrapsFrameNames,
	tilerepoFriendlySupplyUnitsFrameNames,
	tilerepoFriendlyUnitsFrameNames,
	tilerepoGravelRoadsFrameNames,
	tilerepoLightHousesFrameNames,
	tilerepoLookoutTowersFrameNames,
	tilerepoSpecialUnitsFrameNames,
	tilerepoWoodenBridgeRoadsFrameNames,
} from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import type TileEntity from "@/game/entities/TileEntity";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository.ts";
import useBuildingsStorageStore from "@/store/useBuildingsStorageStore";
import useResourcesStore from "@/store/useResourcesStore";
import useSiegeSystemStore from "@/store/useSiegeSystemStore";
import type { EventCategory } from "@/types/ui";
import {
	StatusTypes,
	TileUpgradeBoxSkinTypes,
	TileUpgradeBoxTypes,
	UIContexts,
} from "@/types/ui";
import { resourceTypeToCurrencyType } from "@/utils/formatPrice";
import { translate } from "@/utils/translate";
import { toArray } from "@vueuse/core/index.cjs";
import { storeToRefs } from "pinia";
import { computed, markRaw, ref } from "vue";
import AppNotification from "./AppNotification.vue";

interface Props {
	heading: string;
	description?: string;
	type?: TileUpgradeBoxTypes;
	skin?: TileUpgradeBoxSkinTypes;
	context?: UIContexts;
	iconName: string; // TODO
	notification?: number;
	price?: Partial<Record<ResourceType, number>>;
	isDisabled?: boolean;
	entity?: TileEntity;
	selectedEntity?: TileEntity;
	fewShipyards?: boolean;
	hasEnoughtSettlementLevelForPremiumRoads?: boolean;
	isPromotion?: boolean;
	eventType?: EventCategory;
}

const {
	heading,
	description = null,
	type = TileUpgradeBoxTypes.LIST,
	skin = TileUpgradeBoxSkinTypes.DEFAULT,
	context = UIContexts.BASE,
	iconName,
	notification = 0,
	price = null,
	isDisabled = false,
	entity = null,
	selectedEntity = null,
	fewShipyards = false,
	hasEnoughtSettlementLevelForPremiumRoads = false,
	isPromotion = false,
	eventType,
} = defineProps<Props>();

const buildingsStorageStore = useBuildingsStorageStore();

const siegeSystemStore = storeToRefs(useSiegeSystemStore());
const ssr = markRaw(useSiegeSystemRepository());
const resourcesStore = useResourcesStore();

const supplyLimit = computed((): number => {
	return ssr.supplyLimit + Object.keys(siegeSystemStore.units).length * 0; //aby fungovala reaktivita, ked sa zbura budova
});

const isSupplySatisfied = computed((): boolean => {
	const unitSupplyLimit = entity?.siegeAttributes?.supplyLimit ?? 0;

	return unitSupplyLimit > 0
		? true
		: useSiegeSystemRepository().supplyLimit + unitSupplyLimit >= 0;
});

const showToast = ref<boolean>(false);
const toastMessage = ref<string>("");
const isToastAnimating = ref<boolean>(false);

const tileDescription = computed((): string => {
	return description?.replace(/\{\/?b\}/g, "") ?? "";
});

const roadLimitText = computed((): string => {
	if (!entity) {
		return "";
	}
	const isGravelType =
		tilerepoGravelRoadsFrameNames.has(entity.frameName) ||
		tilerepoWoodenBridgeRoadsFrameNames.has(entity.frameName);

	return translate("ui.increase_more_towns_to_level").replace(
		"{level}",
		isGravelType ? "10" : "24",
	);
});

// NOTE: consumes ...
const consumption = computed((): Map<ResourceType, number> | null =>
	// NOTE: handling when consumption is defined as empty Map in config, to avoid showing empty list
	entity?.consumption?.size ? entity?.consumption : null,
);

const relativeConsumption = computed((): Map<ResourceType, number> | null => {
	// NOTE: entity = what is currently placed on the given spot (x,y)
	// NOTE: selectedEntity = what is currently available in the selection

	// return difference between selected entity and current entity if both have consumption, otherwise return current entity consumption
	return new Map<ResourceType, number>(
		selectedEntity?.consumption?.size && entity?.consumption?.size
			? Array.from(entity.consumption)
					.filter(([resource]: [ResourceType, number]): boolean =>
						[
							ResourceType.FOOD_CONSUMPTION,
							ResourceType.POPULATION_CONSUMPTION,
						].includes(resource),
					)
					.map(
						([resourceType, value]: [ResourceType, number]): [
							ResourceType,
							number,
						] => {
							const selectedEntityConsumptionValue =
								selectedEntity.consumption.get(resourceType);

							return [
								resourceType,
								value - (selectedEntityConsumptionValue ?? 0),
							];
						},
					)
			: Array.from(consumption?.value ?? []).filter(
					([resource]: [ResourceType, number]): boolean =>
						[
							ResourceType.FOOD_CONSUMPTION,
							ResourceType.POPULATION_CONSUMPTION,
						].includes(resource),
				),
	);
});

const hidePrice = computed((): boolean => {
	let entityInStorageCount = 0;
	if (tilerepoFishTrapsFrameNames.has(entity!.frameName)) {
		tilerepoFishTrapsFrameNames.forEach((frameName: FrameName): void => {
			entityInStorageCount += buildingsStorageStore?.[frameName] ?? 0;
		});
	} else if (tilerepoFishingBoatsFrameNames.has(entity!.frameName)) {
		tilerepoFishingBoatsFrameNames.forEach((frameName: FrameName): void => {
			entityInStorageCount += buildingsStorageStore?.[frameName] ?? 0;
		});
	} else if (tilerepoBigFishingBoatsFrameNames.has(entity!.frameName)) {
		tilerepoBigFishingBoatsFrameNames.forEach(
			(frameName: FrameName): void => {
				entityInStorageCount += buildingsStorageStore?.[frameName] ?? 0;
			},
		);
	} else if (tilerepoLightHousesFrameNames.has(entity!.frameName)) {
		tilerepoLightHousesFrameNames.forEach((frameName: FrameName): void => {
			entityInStorageCount += buildingsStorageStore?.[frameName] ?? 0;
		});
	} else if (tilerepoBlastFurnacesFrameNames.has(entity!.frameName)) {
		tilerepoBlastFurnacesFrameNames.forEach(
			(frameName: FrameName): void => {
				entityInStorageCount += buildingsStorageStore?.[frameName] ?? 0;
			},
		);
	} else if (tilerepoLookoutTowersFrameNames.has(entity!.frameName)) {
		tilerepoLookoutTowersFrameNames.forEach(
			(frameName: FrameName): void => {
				entityInStorageCount += buildingsStorageStore?.[frameName] ?? 0;
			},
		);
	} else {
		entityInStorageCount = buildingsStorageStore?.[entity!.frameName] ?? 0;
	}
	return entityInStorageCount > 0;
});

const requiredLevel = computed((): number => {
	return entity?.requirements.has(ResourceType.LEVEL)
		? (entity?.requirements.get(ResourceType.LEVEL) ?? 0)
		: 0;
});

const isContextAvailable = computed((): boolean => {
	return ![UIContexts.LEVEL_UP, UIContexts.UNLOCK_HISTORY].includes(context);
});

const isLevelUp = computed((): boolean => {
	return context === UIContexts.LEVEL_UP;
});

const handleToast = (): void => {
	if (isDisabled) {
		toastMessage.value = "";
		if (isToastAnimating.value) return;

		const entries = Object.entries(price || {}) as Array<
			[ResourceType, number]
		>;

		const missingResourceEntry = entries.find(
			([key, value]: [ResourceType, number]): boolean =>
				!resourcesStore.validateResourceAmount(key, value),
		);

		const missingKey: ResourceType | null =
			missingResourceEntry && missingResourceEntry[0]
				? resourceTypeToCurrencyType(missingResourceEntry[0])
				: null;

		if (entity && entity.frameName === FrameName.MILITARY_SHIPYARD) {
			// Specialna hlaska, ktora je rovnaka ako description.
			const message = translate(
				`${FrameName.MILITARY_SHIPYARD}.description`,
			);
			toastMessage.value = message;
		}

		const unitSupplyLimit = entity?.siegeAttributes?.supplyLimit ?? 0;

		const isSupplyLimitSatisfied =
			unitSupplyLimit > 0
				? true
				: useSiegeSystemRepository().supplyLimit + unitSupplyLimit >= 0;
		if (
			entity &&
			!entity.isLevelRequirementSatisfied(
				entity,
				// true,
			) &&
			type === TileUpgradeBoxTypes.GRID
		) {
			toastMessage.value = translate("ui.available_from_level").replace(
				"{number}",
				requiredLevel.value.toString(),
			);
		} else if (
			entity &&
			entity.requirements.has(ResourceType.SETTLEMENT_LEVEL) &&
			entity.assignedCastleTileEntity &&
			entity.assignedCastleTileEntity.level <
				entity.requirements.get(ResourceType.SETTLEMENT_LEVEL)!
		) {
			toastMessage.value = translate(
				"ui.settlement_level_requirement_not_met",
			);
		} else if (fewShipyards && type === TileUpgradeBoxTypes.GRID) {
			toastMessage.value = translate("ui.build_more_shipyards");
		} else if (
			hasEnoughtSettlementLevelForPremiumRoads &&
			type === TileUpgradeBoxTypes.GRID
		) {
			toastMessage.value = roadLimitText.value;
		} else if (
			entity &&
			entity.buildingLimit &&
			entity.sameBuildingCount +
				(buildingsStorageStore?.[entity.frameName] || 0) >=
				entity.buildingLimit
		) {
			toastMessage.value = translate("ui.building_limit");
		} else if (missingKey) {
			toastMessage.value = translate("event.not_enough_resources");
		} else if (
			entity &&
			tilerepoFriendlyUnitsFrameNames.has(entity.frameName) &&
			!ssr.isInActiveSiegeSystemAreaByTileIndex(entity.index)
		) {
			toastMessage.value = translate("ui.not_in_this_battlefield");
		} else if (
			entity &&
			tilerepoFriendlyUnitsFrameNames.has(entity.frameName) &&
			!isSupplyLimitSatisfied
		) {
			toastMessage.value = translate("ui.insufficient_supply_limit");
		}
		isToastAnimating.value = true;
		showToast.value = true;
		setTimeout((): void => {
			showToast.value = false;
			isToastAnimating.value = false;
		}, 1500); // Show toast for 1.5 seconds
	}
};
</script>

<style lang="scss" scoped>
.upgrade-box {
	position: relative;
	box-shadow: inset 0 0 0.75rem 0 #1d1e21;
	border-width: 0.05rem;
	border-style: solid;
	border-image-slice: 1;
	@include clickable-element;

	&--default,
	&--terrain {
		border-image-source: linear-gradient(to top, #3f5941, #5e7a61);
		background-image: linear-gradient(to top, #2e3f30, #4d6d51);
	}

	&--building {
		border-image-source: linear-gradient(to top, #61667f, #787c8b);
		background-image: linear-gradient(to top, #3b3c48, #595d6d);

		&-premium {
			box-shadow:
				0 0 1.25rem 0 rgba(153, 33, 203, 0.8),
				inset 0 0 0.5rem 0 rgba(49, 37, 22, 0.6);
			border-image-source: linear-gradient(to top, #9e68b7, #b080cb);
			background-image: linear-gradient(to top, #543464, #8d59a4);
		}
		&-event {
			flex-wrap: wrap;
			.upgrade-box__content-wrapper {
				width: 75%;
			}
			:deep(.toast-message) {
				position: absolute !important;
				top: 2rem !important;
				left: 40% !important;
			}
		}
	}
	&--friendly-unit {
		flex-wrap: wrap;
		border-image-source: linear-gradient(
			0deg,
			rgba(63, 107, 157, 1) 0%,
			rgba(54, 93, 137, 1) 100%
		);
		background-image: linear-gradient(
			0deg,
			rgba(27, 50, 76, 1) 0%,
			rgba(42, 80, 124, 1) 100%
		);
	}
	&[data-event-type="christmas"] {
		&.upgrade-box--building-event {
			border-image-source: linear-gradient(
				0deg,
				rgba(111, 14, 18, 1) 0%,
				rgba(165, 29, 35, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(64, 6, 9, 1) 0%,
				rgba(102, 15, 20, 1) 100%
			);
		}
	}
	&[data-event-type="easter"] {
		&.upgrade-box--building-event {
			border-image-source: linear-gradient(
				0deg,
				rgba(90, 111, 8, 1) 0%,
				rgba(120, 149, 8, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(34, 42, 4, 1) 0%,
				rgba(61, 76, 3, 1) 100%
			);
		}
	}
	&[data-event-type="fishing"] {
		&.upgrade-box--building-event {
			border-image-source: linear-gradient(
				0deg,
				rgba(15, 86, 112, 1) 0%,
				rgba(30, 129, 166, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(6, 48, 64, 1) 0%,
				rgba(15, 79, 102, 1) 100%
			);
		}
	}
	&[data-event-type="firefest"] {
		&.upgrade-box--building-event {
			border-image-source: linear-gradient(
				0deg,
				rgba(189, 144, 50, 1) 0%,
				rgba(240, 211, 134, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(111, 33, 15, 1) 0%,
				rgba(175, 64, 35, 1) 100%
			);
		}
	}
	&[data-event-type="solarfair"] {
		&.upgrade-box--building-event {
			border-image-source: linear-gradient(
				0deg,
				rgba(189, 144, 50, 1) 0%,
				rgba(240, 211, 134, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(101, 60, 17, 1) 0%,
				rgba(181, 113, 38, 1) 100%
			);
		}
		.event-building-label {
			&::after {
				mix-blend-mode: multiply;
				background: linear-gradient(
					90deg,
					rgba(122, 74, 148, 0) 0%,
					rgba(122, 74, 148, 0.8) 15%,
					rgba(122, 74, 148, 1) 30%,
					rgba(122, 74, 148, 1) 60%,
					rgba(122, 74, 148, 0.8) 85%,
					rgba(122, 74, 148, 0) 100%
				);
			}
		}
	}
	&[data-event-type="hunting"] {
		&.upgrade-box--building-event {
			border-image-source: linear-gradient(
				0deg,
				rgba(15, 112, 20, 1) 0%,
				rgba(25, 150, 32, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(15, 53, 15, 1) 0%,
				rgba(30, 85, 30, 1) 100%
			);
		}
		.event-building-label {
			&::after {
				mix-blend-mode: multiply;
				background: linear-gradient(
					90deg,
					rgba(122, 74, 148, 0) 0%,
					rgba(122, 74, 148, 0.8) 15%,
					rgba(122, 74, 148, 1) 30%,
					rgba(122, 74, 148, 1) 60%,
					rgba(122, 74, 148, 0.8) 85%,
					rgba(122, 74, 148, 0) 100%
				);
			}
		}
	}
	&[data-event-type="halloween"] {
		&.upgrade-box--building-event {
			border-image-source: linear-gradient(
				0deg,
				rgba(59, 29, 130, 1) 0%,
				rgba(97, 52, 201, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(30, 17, 60, 1) 0%,
				rgba(75, 46, 141, 1) 100%
			);
		}
	}
	&[data-event-type="pirates"] {
		&.upgrade-box--building-event {
			border-image-source: linear-gradient(
				0deg,
				rgba(24, 66, 114, 1) 0%,
				rgba(34, 93, 158, 1) 100%
			);
			background-image: linear-gradient(
				0deg,
				rgba(10, 35, 64, 1) 0%,
				rgba(26, 71, 122, 1) 100%
			);
		}
		.event-building-label {
			&::after {
				mix-blend-mode: multiply;
				background: linear-gradient(
					90deg,
					rgba(91, 115, 9, 0.05) 0%,
					rgba(91, 115, 9, 0.8) 15%,
					rgba(91, 115, 9, 1) 30%,
					rgba(91, 115, 9, 1) 70%,
					rgba(91, 115, 9, 0.8) 85%,
					rgba(91, 115, 9, 0.05) 100%
				);
			}
		}
	}
	&::before {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.6;
		z-index: 0;
		// NOTE: Color-burn blending is better for dark colors
		mix-blend-mode: color-burn;
		background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
			repeat center;
		background-size: 40%;
		width: 100%;
		height: 100%;
		content: "";
	}

	&--list {
		position: relative;
		@include flex-row-start;
		flex: 1 1 auto;
		align-items: flex-start;
		gap: 0.5rem;
		padding: 0.438rem 0.438rem 0.438rem 0.531rem;
		width: 100%;
		max-width: 25rem;
		min-height: 5.563rem;

		.upgrade-box {
			&__content {
				.content-container {
					align-items: flex-start;
					padding-top: 0.25rem;
				}
			}
		}
	}

	&--grid {
		@include flex-col-center;
		width: 5.625rem;
		height: 6rem;

		.upgrade-box {
			&__heading {
				display: -webkit-box;
				font-weight: 500;
				font-size: 0.625rem;
				line-height: 1rem;
				text-align: center;
				text-transform: capitalize;
				-webkit-box-orient: vertical;
				-webkit-line-clamp: 1;
				overflow: hidden;
			}

			&__image {
				margin-top: 0.125rem;
			}

			&__content-wrapper {
				padding-top: 0;
				width: 100%;
				min-height: auto;

				.content-container {
					padding: 0 0.25rem;
					.upgrade-box__heading {
						margin-top: 0.25rem;
					}
				}
			}
		}
	}

	&:not(.upgrade-box--disabled) {
		cursor: pointer;
	}

	&--disabled {
		cursor: not-allowed;
		overflow: visible;
		& > *:not(.toast-message) {
			opacity: 0.5;
		}
	}

	&__image {
		position: relative;
		flex-shrink: 0;
		margin-top: -0.125rem;

		.notification {
			@include flex-col-center;
			position: absolute;
			right: -0.375rem;
			bottom: -0.375rem;
			border-radius: 50%;
			background: hotpink;
			padding: 0.5rem;
			width: 1.5rem;
			height: 1.5rem;
			color: white;
			font-weight: bold;
		}
	}

	&__content {
		@include flex-col-start;
		flex: 1 1 auto;
		gap: 0.25rem;

		&-wrapper {
			display: flex;
			position: relative;
			flex: 1 1 auto;
			padding-top: 0.375rem;
			width: 80%;
			min-height: calc(5.563rem - 0.875rem);
		}

		.content-container {
			width: 100%;
			@include flex-col-start;
			flex: 1 1 auto;
			gap: 0.25rem;
		}
	}

	&__heading {
		color: #f2dcb5;
		font-weight: 800;
		font-size: 1rem;
		line-height: 1rem;
		text-shadow: 0.07rem 0.07rem 0.1rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}

	&__description {
		padding-right: 0.5rem;
		color: #f2dcb5;
		font-size: 0.75rem;
		line-height: 1rem;
	}
	&__limit {
		font-weight: bold;
		@include flex-row-start;
		gap: 0.25rem;
		.full-limit {
			color: #ff3d3d;
		}
	}

	&__price {
		width: 100%;
		@include flex-row-end;
		flex-wrap: wrap;
		gap: 0.125rem;
		margin-top: 0.25rem;
		font-size: 0.875rem;

		&-label {
			color: #f2dcb5;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}

		:deep(.price__value) {
			font-size: 0.875rem;
		}
	}
	&__required {
		width: 100%;
		text-align: right;
	}
	:deep(.flex-break) {
		flex-basis: 100%;
		height: 0;
	}

	.event-building-label {
		@include flex-row-center;
		position: relative;
		align-self: stretch;
		mask-image: linear-gradient(
			to right,
			transparent 2%,
			black 20%,
			black 80%,
			transparent 98%
		);
		width: 100%;
		height: 1.875rem;

		span {
			position: relative;
			z-index: 1;
			padding-bottom: 0.25rem;
			color: #f4d278;
			font-weight: 800;
			font-size: 0.906rem;
			line-height: 0.906rem;
			text-align: center;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			@include background(
				url($path-textures + "silk-flag-texture-bg.avif"),
				cover,
				center center,
				no-repeat
			);
			z-index: 0;
			width: 100%;
			height: 100%;
			content: "";
		}

		&::after {
			position: absolute;
			top: 0;
			left: 0;
			mix-blend-mode: hard-light;
			background: hsl(136, 79%, 15%);
			width: 100%;
			height: 100%;
			content: "";
		}
	}
	.red-notification {
		position: absolute;
		top: -0.25rem;
		right: -0.25rem;
	}
	.no-supply {
		flex: 0 0 100%;
		border: solid 0.05rem #cf0032;
		background-color: #550014;
		padding: 0rem 0.5rem 0.25rem 0.5rem;
		font-weight: bold;
		font-size: 0.935rem;
		text-align: center;
		text-transform: none;
	}
}
// Deep CSS styling to make Vue ToastMessage look like Phaser ToastMessage
:deep(.toast-message) {
	position: absolute !important;
	top: 50% !important;
	left: 50% !important;
	transform: translate(-50%, -50%) !important;
}
</style>
